import { useInView } from '../hooks/useInView';
import { processSteps } from '../data/mock';
import { MessageCircle, BookOpen, GraduationCap, ShieldCheck, Plane } from 'lucide-react';

const iconMap = { MessageCircle, BookOpen, GraduationCap, ShieldCheck, Plane };

export const ProcessTimeline = () => {
  const [ref, isInView] = useInView();

  return (
    <section id="process" className="relative py-24 bg-white overflow-hidden">
      {/* Subtle background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#D4A853]/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#243B6A]/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref}>
        {/* Section Header */}
        <div
          className={`text-center mb-20 transition-[opacity,transform] duration-700 ${
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span className="text-[#D4A853] font-semibold text-sm uppercase tracking-[0.2em]">How It Works</span>
          <h2
            className="text-4xl sm:text-5xl font-bold text-[#0A1628] mt-3 mb-5"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            Your Journey Starts Here
          </h2>
          <p className="text-slate-500 text-lg max-w-2xl mx-auto leading-relaxed">
            A clear, proven 5-step process to take you from aspiration to admission. We handle the complexity, you focus on your dreams.
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Connecting line - Desktop */}
          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[#D4A853]/50 via-[#243B6A]/30 to-[#D4A853]/50 hidden lg:block" />

          {/* Connecting line - Mobile */}
          <div className="absolute left-8 top-0 bottom-0 w-px bg-gradient-to-b from-[#D4A853]/50 via-[#243B6A]/30 to-[#D4A853]/50 lg:hidden" />

          <div className="space-y-16 lg:space-y-20">
            {processSteps.map((step, index) => {
              const Icon = iconMap[step.icon];
              const isEven = index % 2 === 0;

              return (
                <div
                  key={step.step}
                  className={`relative transition-[opacity,transform] duration-700 ${
                    isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{ transitionDelay: `${index * 200}ms` }}
                >
                  {/* Desktop layout */}
                  <div className="hidden lg:grid grid-cols-[1fr_80px_1fr] items-center">
                    {/* Left content */}
                    <div className={isEven ? 'pr-12' : 'order-3 pl-12'}>
                      <div className={`max-w-md ${isEven ? 'ml-auto text-right' : ''}`}>
                        <span className="text-[#D4A853] font-bold text-sm tracking-wide" style={{ fontFamily: 'Poppins, sans-serif' }}>
                          Step {step.step}
                        </span>
                        <h3
                          className="text-2xl font-bold text-[#0A1628] mt-1 mb-3"
                          style={{ fontFamily: 'Poppins, sans-serif' }}
                        >
                          {step.title}
                        </h3>
                        <p className="text-slate-500 leading-relaxed">{step.description}</p>
                      </div>
                    </div>

                    {/* Center icon */}
                    <div className="flex justify-center">
                      <div className="relative z-10 w-16 h-16 bg-[#0A1628] rounded-2xl flex items-center justify-center shadow-lg shadow-[#0A1628]/25 group hover:bg-[#D4A853] transition-colors duration-400">
                        <Icon className="w-7 h-7 text-[#E8C87A] group-hover:text-[#0A1628] transition-colors duration-400" />
                      </div>
                    </div>

                    {/* Right space */}
                    <div className={isEven ? 'order-3' : ''} />
                  </div>

                  {/* Mobile layout */}
                  <div className="lg:hidden flex gap-6">
                    <div className="relative z-10 flex-shrink-0">
                      <div className="w-16 h-16 bg-[#0A1628] rounded-2xl flex items-center justify-center shadow-lg shadow-[#0A1628]/25">
                        <Icon className="w-7 h-7 text-[#E8C87A]" />
                      </div>
                    </div>
                    <div className="pt-1">
                      <span className="text-[#D4A853] font-bold text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        Step {step.step}
                      </span>
                      <h3
                        className="text-xl font-bold text-[#0A1628] mt-1 mb-2"
                        style={{ fontFamily: 'Poppins, sans-serif' }}
                      >
                        {step.title}
                      </h3>
                      <p className="text-slate-500 leading-relaxed text-sm">{step.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
